import { AIModule } from '../types';

export const DATA_PIPELINE_MLOPS_MODULE: AIModule = {
  id: 'data-pipeline-mlops',
  title: 'Data Pipeline & MLOps Fundamentals',
  description: 'Learn to build scalable data pipelines and implement MLOps practices for production machine learning systems.',
  duration: '4 hours',
  difficulty: 'intermediate',
  order: 1,
  content: `
# Data Pipeline & MLOps Fundamentals

## Overview
This module covers the essential concepts of building data pipelines and implementing MLOps practices for production-ready machine learning systems.

## Learning Objectives
- Understand data pipeline architecture and design patterns
- Learn MLOps best practices and tools
- Implement CI/CD for machine learning projects
- Master data versioning and model versioning
- Deploy and monitor ML models in production

## 1. Data Pipeline Architecture

### Understanding Data Pipelines
\`\`\`python
import pandas as pd
from airflow import DAG
from airflow.operators.python_operator import PythonOperator
from datetime import datetime, timedelta

def extract_data():
    """Extract data from source systems"""
    # Simulate data extraction
    data = pd.read_csv('raw_data.csv')
    return data

def transform_data(**context):
    """Transform and clean data"""
    data = context['task_instance'].xcom_pull(task_ids='extract_task')
    
    # Data cleaning and transformation
    cleaned_data = data.dropna()
    cleaned_data['processed_date'] = datetime.now()
    
    return cleaned_data

def load_data(**context):
    """Load data to destination"""
    processed_data = context['task_instance'].xcom_pull(task_ids='transform_task')
    
    # Save to database or data warehouse
    processed_data.to_sql('processed_table', con=db_connection)

# Define DAG
default_args = {
    'owner': 'data-team',
    'depends_on_past': False,
    'start_date': datetime(2024, 1, 1),
    'retries': 1,
    'retry_delay': timedelta(minutes=5)
}

dag = DAG(
    'data_pipeline_dag',
    default_args=default_args,
    description='ETL Data Pipeline',
    schedule_interval=timedelta(days=1)
)

# Define tasks
extract_task = PythonOperator(
    task_id='extract_task',
    python_callable=extract_data,
    dag=dag
)

transform_task = PythonOperator(
    task_id='transform_task',
    python_callable=transform_data,
    dag=dag
)

load_task = PythonOperator(
    task_id='load_task',
    python_callable=load_data,
    dag=dag
)

# Set task dependencies
extract_task >> transform_task >> load_task
\`\`\`

### Data Quality and Validation
\`\`\`python
import great_expectations as ge
from evidently.report import Report
from evidently.metric_preset import DataDriftPreset

class DataQualityChecker:
    def __init__(self):
        self.context = ge.get_context()
    
    def validate_data_quality(self, df, expectations_suite):
        """Validate data quality using Great Expectations"""
        dataset = ge.from_pandas(df)
        
        validation_result = dataset.validate(
            expectation_suite=expectations_suite
        )
        
        return validation_result.success
    
    def detect_data_drift(self, reference_data, current_data):
        """Detect data drift using Evidently"""
        report = Report(metrics=[DataDriftPreset()])
        
        report.run(
            reference_data=reference_data,
            current_data=current_data
        )
        
        return report

# Example usage
quality_checker = DataQualityChecker()

# Define expectations
expectations_suite = {
    "expectation_suite_name": "data_quality_suite",
    "expectations": [
        {
            "expectation_type": "expect_column_values_to_not_be_null",
            "kwargs": {"column": "user_id"}
        },
        {
            "expectation_type": "expect_column_values_to_be_between",
            "kwargs": {"column": "age", "min_value": 0, "max_value": 120}
        }
    ]
}

# Validate data
is_valid = quality_checker.validate_data_quality(df, expectations_suite)
\`\`\`

## 2. MLOps Best Practices

### Model Versioning and Registry
\`\`\`python
import mlflow
import mlflow.sklearn
from sklearn.ensemble import RandomForestClassifier
from sklearn.metrics import accuracy_score
import joblib

class MLModelRegistry:
    def __init__(self, tracking_uri="sqlite:///mlflow.db"):
        mlflow.set_tracking_uri(tracking_uri)
        
    def train_and_register_model(self, X_train, y_train, X_test, y_test):
        """Train model and register in MLflow"""
        
        with mlflow.start_run():
            # Train model
            model = RandomForestClassifier(n_estimators=100, random_state=42)
            model.fit(X_train, y_train)
            
            # Make predictions
            predictions = model.predict(X_test)
            accuracy = accuracy_score(y_test, predictions)
            
            # Log parameters and metrics
            mlflow.log_param("n_estimators", 100)
            mlflow.log_param("random_state", 42)
            mlflow.log_metric("accuracy", accuracy)
            
            # Log model
            mlflow.sklearn.log_model(
                model, 
                "random_forest_model",
                registered_model_name="ProductionClassifier"
            )
            
            return model, accuracy
    
    def load_model_version(self, model_name, version):
        """Load specific model version"""
        model_uri = f"models:/{model_name}/{version}"
        model = mlflow.sklearn.load_model(model_uri)
        return model
    
    def promote_model_to_production(self, model_name, version):
        """Promote model version to production"""
        client = mlflow.tracking.MlflowClient()
        
        client.transition_model_version_stage(
            name=model_name,
            version=version,
            stage="Production"
        )

# Example usage
registry = MLModelRegistry()
model, accuracy = registry.train_and_register_model(X_train, y_train, X_test, y_test)

# Promote to production if accuracy meets threshold
if accuracy > 0.85:
    registry.promote_model_to_production("ProductionClassifier", 1)
\`\`\`

### CI/CD for ML Projects
\`\`\`yaml
# .github/workflows/ml-pipeline.yml
name: ML Pipeline

on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  test:
    runs-on: ubuntu-latest
    
    steps:
    - uses: actions/checkout@v2
    
    - name: Set up Python
      uses: actions/setup-python@v2
      with:
        python-version: 3.8
    
    - name: Install dependencies
      run: |
        pip install -r requirements.txt
    
    - name: Run data validation tests
      run: |
        python -m pytest tests/test_data_validation.py
    
    - name: Run model tests
      run: |
        python -m pytest tests/test_model.py
    
    - name: Train and validate model
      run: |
        python scripts/train_model.py
        python scripts/validate_model.py
  
  deploy:
    needs: test
    runs-on: ubuntu-latest
    if: github.ref == 'refs/heads/main'
    
    steps:
    - uses: actions/checkout@v2
    
    - name: Deploy to staging
      run: |
        python scripts/deploy_model.py --env staging
    
    - name: Run integration tests
      run: |
        python tests/test_integration.py --env staging
    
    - name: Deploy to production
      run: |
        python scripts/deploy_model.py --env production
\`\`\`

## 3. Model Monitoring and Observability

### Performance Monitoring
\`\`\`python
import logging
import time
from datetime import datetime
import psutil
import numpy as np
from sklearn.metrics import accuracy_score, precision_score, recall_score

class ModelMonitor:
    def __init__(self, model_name):
        self.model_name = model_name
        self.logger = self._setup_logging()
        
    def _setup_logging(self):
        """Setup logging for model monitoring"""
        logger = logging.getLogger(f"model_monitor_{self.model_name}")
        handler = logging.StreamHandler()
        formatter = logging.Formatter(
            '%(asctime)s - %(name)s - %(levelname)s - %(message)s'
        )
        handler.setFormatter(formatter)
        logger.addHandler(handler)
        logger.setLevel(logging.INFO)
        return logger
    
    def log_prediction(self, input_data, prediction, confidence=None):
        """Log individual predictions"""
        log_entry = {
            'timestamp': datetime.now().isoformat(),
            'model_name': self.model_name,
            'input_features': input_data.tolist() if hasattr(input_data, 'tolist') else input_data,
            'prediction': prediction,
            'confidence': confidence,
            'system_metrics': self._get_system_metrics()
        }
        
        self.logger.info(f"Prediction logged: {log_entry}")
        return log_entry
    
    def _get_system_metrics(self):
        """Get system performance metrics"""
        return {
            'cpu_percent': psutil.cpu_percent(),
            'memory_percent': psutil.virtual_memory().percent,
            'disk_usage': psutil.disk_usage('/').percent
        }
    
    def evaluate_model_performance(self, y_true, y_pred):
        """Evaluate and log model performance metrics"""
        metrics = {
            'accuracy': accuracy_score(y_true, y_pred),
            'precision': precision_score(y_true, y_pred, average='weighted'),
            'recall': recall_score(y_true, y_pred, average='weighted'),
            'timestamp': datetime.now().isoformat()
        }
        
        self.logger.info(f"Performance metrics: {metrics}")
        
        # Alert if performance drops below threshold
        if metrics['accuracy'] < 0.8:
            self.logger.warning(f"Model accuracy below threshold: {metrics['accuracy']}")
        
        return metrics

# Example usage
monitor = ModelMonitor("production_classifier")

# Log predictions during inference
for data, true_label in test_data:
    prediction = model.predict([data])
    confidence = model.predict_proba([data]).max()
    
    monitor.log_prediction(data, prediction[0], confidence)

# Evaluate performance periodically
all_predictions = [model.predict([data]) for data, _ in test_data]
all_true_labels = [label for _, label in test_data]

performance = monitor.evaluate_model_performance(all_true_labels, all_predictions)
\`\`\`

## 4. Infrastructure as Code

### Docker for ML Applications
\`\`\`dockerfile
# Dockerfile for ML application
FROM python:3.8-slim

WORKDIR /app

# Install system dependencies
RUN apt-get update && apt-get install -y \\
    gcc \\
    && rm -rf /var/lib/apt/lists/*

# Copy requirements and install Python dependencies
COPY requirements.txt .
RUN pip install --no-cache-dir -r requirements.txt

# Copy application code
COPY . .

# Create non-root user
RUN useradd --create-home --shell /bin/bash mluser
USER mluser

# Expose port
EXPOSE 8000

# Health check
HEALTHCHECK --interval=30s --timeout=30s --start-period=5s --retries=3 \\
    CMD curl -f http://localhost:8000/health || exit 1

# Run application
CMD ["uvicorn", "main:app", "--host", "0.0.0.0", "--port", "8000"]
\`\`\`

### Kubernetes Deployment
\`\`\`yaml
# k8s/ml-deployment.yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: ml-model-api
spec:
  replicas: 3
  selector:
    matchLabels:
      app: ml-model-api
  template:
    metadata:
      labels:
        app: ml-model-api
    spec:
      containers:
      - name: ml-api
        image: ml-model:latest
        ports:
        - containerPort: 8000
        env:
        - name: MODEL_VERSION
          value: "v1.0.0"
        resources:
          requests:
            memory: "512Mi"
            cpu: "500m"
          limits:
            memory: "1Gi"
            cpu: "1000m"
        livenessProbe:
          httpGet:
            path: /health
            port: 8000
          initialDelaySeconds: 30
          periodSeconds: 10
        readinessProbe:
          httpGet:
            path: /ready
            port: 8000
          initialDelaySeconds: 5
          periodSeconds: 5

---
apiVersion: v1
kind: Service
metadata:
  name: ml-model-service
spec:
  selector:
    app: ml-model-api
  ports:
    - protocol: TCP
      port: 80
      targetPort: 8000
  type: LoadBalancer
\`\`\`

## Hands-On Exercises

### Exercise 1: Build a Data Pipeline
1. Create an Airflow DAG for processing customer data
2. Implement data quality checks
3. Add monitoring and alerting

### Exercise 2: Model Deployment Pipeline
1. Set up MLflow for model tracking
2. Create a CI/CD pipeline for model deployment
3. Implement A/B testing for model versions

### Exercise 3: Production Monitoring
1. Deploy a model with monitoring
2. Set up performance alerting
3. Implement automated model retraining

## Summary
You've learned the fundamentals of data pipeline engineering and MLOps practices essential for production machine learning systems.
`,
  objectives: [
    'Design and implement scalable data pipelines',
    'Understand MLOps best practices and tools',
    'Implement CI/CD for machine learning projects',
    'Set up model monitoring and observability',
    'Deploy ML models using containerization and orchestration'
  ],
  quiz: {
    questions: [
      {
        id: 'q1',
        question: 'What is the primary purpose of MLOps?',
        options: [
          'To train machine learning models',
          'To operationalize machine learning workflows and ensure reliable deployment',
          'To visualize data',
          'To clean datasets'
        ],
        correctAnswer: 1,
        explanation: 'MLOps focuses on operationalizing ML workflows, ensuring reliable deployment, monitoring, and maintenance of ML systems in production.'
      },
      {
        id: 'q2',
        question: 'Which tool is commonly used for model versioning and experiment tracking?',
        options: [
          'Docker',
          'Kubernetes',
          'MLflow',
          'Apache Airflow'
        ],
        correctAnswer: 2,
        explanation: 'MLflow is a popular platform for managing the ML lifecycle, including experimentation, reproducibility, deployment, and model registry.'
      }
    ]
  }
};

export const ADVANCED_MLOPS_MODULE: AIModule = {
  id: 'advanced-mlops',
  title: 'Advanced MLOps & Model Deployment',
  description: 'Master advanced MLOps techniques including automated retraining, feature stores, and multi-model serving.',
  duration: '5 hours',
  difficulty: 'advanced',
  order: 2,
  content: `
# Advanced MLOps & Model Deployment

## Overview
This advanced module covers sophisticated MLOps techniques for enterprise-scale machine learning operations.

## Learning Objectives
- Implement feature stores and feature engineering pipelines
- Set up automated model retraining and validation
- Master multi-model serving and canary deployments
- Implement advanced monitoring and observability
- Design fault-tolerant ML systems

## 1. Feature Store Implementation

### Building a Feature Store
\`\`\`python
import feast
from feast import Entity, Feature, FeatureView, ValueType
from feast.data_source import FileSource
import pandas as pd
from datetime import datetime, timedelta

# Define entities
user_entity = Entity(
    name="user_id",
    value_type=ValueType.INT64,
    description="User identifier"
)

# Define feature views
user_features_source = FileSource(
    path="user_features.parquet",
    event_timestamp_column="event_timestamp",
    created_timestamp_column="created_timestamp"
)

user_features_view = FeatureView(
    name="user_features",
    entities=["user_id"],
    ttl=timedelta(days=30),
    features=[
        Feature(name="age", dtype=ValueType.INT64),
        Feature(name="income", dtype=ValueType.DOUBLE),
        Feature(name="credit_score", dtype=ValueType.INT64),
        Feature(name="transaction_count_7d", dtype=ValueType.INT64),
        Feature(name="avg_transaction_amount_7d", dtype=ValueType.DOUBLE)
    ],
    online=True,
    batch_source=user_features_source
)

class FeatureStoreManager:
    def __init__(self, repo_path="feature_repo"):
        self.store = feast.FeatureStore(repo_path=repo_path)
    
    def register_features(self):
        """Register feature definitions"""
        self.store.apply([user_entity, user_features_view])
    
    def get_online_features(self, entity_df):
        """Get features for online serving"""
        feature_vector = self.store.get_online_features(
            features=[
                "user_features:age",
                "user_features:income", 
                "user_features:credit_score",
                "user_features:transaction_count_7d",
                "user_features:avg_transaction_amount_7d"
            ],
            entity_df=entity_df
        )
        return feature_vector.to_df()
    
    def get_historical_features(self, entity_df):
        """Get features for training"""
        training_df = self.store.get_historical_features(
            entity_df=entity_df,
            features=[
                "user_features:age",
                "user_features:income",
                "user_features:credit_score", 
                "user_features:transaction_count_7d",
                "user_features:avg_transaction_amount_7d"
            ]
        ).to_df()
        return training_df

# Example usage
fs_manager = FeatureStoreManager()
fs_manager.register_features()

# Get features for prediction
entity_df = pd.DataFrame({"user_id": [1001, 1002, 1003]})
features = fs_manager.get_online_features(entity_df)
\`\`\`

## 2. Automated Model Retraining

### Continuous Training Pipeline
\`\`\`python
import mlflow
import pandas as pd
from sklearn.ensemble import RandomForestClassifier
from sklearn.metrics import accuracy_score, f1_score
from datetime import datetime, timedelta
import schedule
import time

class AutomatedRetraining:
    def __init__(self, model_name, performance_threshold=0.85):
        self.model_name = model_name
        self.performance_threshold = performance_threshold
        self.mlflow_client = mlflow.tracking.MlflowClient()
        
    def check_data_drift(self, reference_data, current_data):
        """Check for data drift using statistical tests"""
        from scipy import stats
        
        drift_detected = False
        drift_metrics = {}
        
        for column in reference_data.columns:
            if reference_data[column].dtype in ['int64', 'float64']:
                # Use Kolmogorov-Smirnov test for numerical features
                statistic, p_value = stats.ks_2samp(
                    reference_data[column], 
                    current_data[column]
                )
                drift_metrics[column] = {'statistic': statistic, 'p_value': p_value}
                
                if p_value < 0.05:  # Significant drift detected
                    drift_detected = True
        
        return drift_detected, drift_metrics
    
    def evaluate_current_model(self, test_data, test_labels):
        """Evaluate current production model performance"""
        # Load current production model
        production_model = mlflow.sklearn.load_model(
            f"models:/{self.model_name}/Production"
        )
        
        predictions = production_model.predict(test_data)
        accuracy = accuracy_score(test_labels, predictions)
        f1 = f1_score(test_labels, predictions, average='weighted')
        
        return {
            'accuracy': accuracy,
            'f1_score': f1,
            'needs_retraining': accuracy < self.performance_threshold
        }
    
    def retrain_model(self, training_data, training_labels):
        """Retrain model with new data"""
        with mlflow.start_run():
            # Train new model
            new_model = RandomForestClassifier(
                n_estimators=100,
                random_state=42
            )
            new_model.fit(training_data, training_labels)
            
            # Log model and metrics
            mlflow.sklearn.log_model(
                new_model,
                "retrained_model",
                registered_model_name=self.model_name
            )
            
            # Get model version
            latest_version = self.mlflow_client.get_latest_versions(
                self.model_name,
                stages=["None"]
            )[0]
            
            return new_model, latest_version.version
    
    def validate_and_promote(self, model_version, validation_data, validation_labels):
        """Validate new model and promote if better"""
        # Load new model
        new_model = mlflow.sklearn.load_model(
            f"models:/{self.model_name}/{model_version}"
        )
        
        # Load current production model for comparison
        try:
            current_model = mlflow.sklearn.load_model(
                f"models:/{self.model_name}/Production"
            )
            current_predictions = current_model.predict(validation_data)
            current_accuracy = accuracy_score(validation_labels, current_predictions)
        except:
            current_accuracy = 0  # No production model exists
        
        # Evaluate new model
        new_predictions = new_model.predict(validation_data)
        new_accuracy = accuracy_score(validation_labels, new_predictions)
        
        # Promote if better
        if new_accuracy > current_accuracy and new_accuracy > self.performance_threshold:
            self.mlflow_client.transition_model_version_stage(
                name=self.model_name,
                version=model_version,
                stage="Production"
            )
            return True, new_accuracy
        
        return False, new_accuracy
    
    def automated_retraining_pipeline(self):
        """Main automated retraining pipeline"""
        print(f"Starting automated retraining check at {datetime.now()}")
        
        # Load data (this would come from your data pipeline)
        current_data = self.load_current_data()
        reference_data = self.load_reference_data()
        
        # Check for data drift
        drift_detected, drift_metrics = self.check_data_drift(reference_data, current_data)
        
        # Evaluate current model
        test_data, test_labels = self.load_test_data()
        performance = self.evaluate_current_model(test_data, test_labels)
        
        # Trigger retraining if needed
        if drift_detected or performance['needs_retraining']:
            print("Retraining triggered due to drift or performance degradation")
            
            training_data, training_labels = self.load_training_data()
            new_model, version = self.retrain_model(training_data, training_labels)
            
            # Validate and promote
            validation_data, validation_labels = self.load_validation_data()
            promoted, accuracy = self.validate_and_promote(
                version, validation_data, validation_labels
            )
            
            if promoted:
                print(f"New model promoted to production with accuracy: {accuracy}")
            else:
                print(f"New model not promoted. Accuracy: {accuracy}")
        else:
            print("No retraining needed")

# Schedule automated retraining
retrainer = AutomatedRetraining("fraud_detection_model")

# Run daily at 2 AM
schedule.every().day.at("02:00").do(retrainer.automated_retraining_pipeline)

# Keep the scheduler running
while True:
    schedule.run_pending()
    time.sleep(60)
\`\`\`

## 3. Multi-Model Serving & A/B Testing

### Model Serving with Canary Deployments
\`\`\`python
import random
import time
from typing import Dict, Any
import numpy as np
from dataclasses import dataclass
from enum import Enum

class DeploymentStrategy(Enum):
    BLUE_GREEN = "blue_green"
    CANARY = "canary"
    A_B_TEST = "a_b_test"

@dataclass
class ModelVersion:
    name: str
    version: str
    model_path: str
    traffic_percentage: float
    performance_metrics: Dict[str, float]

class MultiModelServing:
    def __init__(self):
        self.models: Dict[str, ModelVersion] = {}
        self.deployment_strategy = DeploymentStrategy.CANARY
        self.metrics_collector = MetricsCollector()
        
    def register_model(self, model_version: ModelVersion):
        """Register a new model version"""
        self.models[f"{model_version.name}_{model_version.version}"] = model_version
        print(f"Registered model {model_version.name} version {model_version.version}")
    
    def route_traffic(self, model_name: str) -> ModelVersion:
        """Route traffic based on deployment strategy"""
        available_models = [
            model for model in self.models.values() 
            if model.name == model_name
        ]
        
        if not available_models:
            raise ValueError(f"No models available for {model_name}")
        
        # Traffic routing based on percentages
        random_value = random.random() * 100
        cumulative_percentage = 0
        
        for model in available_models:
            cumulative_percentage += model.traffic_percentage
            if random_value <= cumulative_percentage:
                return model
        
        # Fallback to first model
        return available_models[0]
    
    def predict(self, model_name: str, input_data: np.ndarray) -> Dict[str, Any]:
        """Make prediction with traffic routing"""
        start_time = time.time()
        
        # Route to appropriate model version
        selected_model = self.route_traffic(model_name)
        
        # Load and predict (simplified - in practice, models would be cached)
        model = self.load_model(selected_model.model_path)
        prediction = model.predict(input_data)
        
        # Collect metrics
        inference_time = time.time() - start_time
        self.metrics_collector.record_prediction(
            model_name=selected_model.name,
            model_version=selected_model.version,
            inference_time=inference_time,
            input_size=len(input_data)
        )
        
        return {
            'prediction': prediction.tolist(),
            'model_version': selected_model.version,
            'inference_time': inference_time
        }
    
    def update_traffic_split(self, model_name: str, version_weights: Dict[str, float]):
        """Update traffic split between model versions"""
        total_weight = sum(version_weights.values())
        
        for model_key, model in self.models.items():
            if model.name == model_name:
                version = model.version
                if version in version_weights:
                    model.traffic_percentage = (version_weights[version] / total_weight) * 100
                else:
                    model.traffic_percentage = 0
        
        print(f"Updated traffic split for {model_name}: {version_weights}")
    
    def canary_deployment(self, model_name: str, new_version: str, 
                         canary_percentage: float = 10.0):
        """Implement canary deployment strategy"""
        print(f"Starting canary deployment for {model_name} v{new_version}")
        
        # Update traffic split for canary
        self.update_traffic_split(model_name, {
            "v1.0": 100 - canary_percentage,
            new_version: canary_percentage
        })
        
        # Monitor canary performance
        self.monitor_canary(model_name, new_version)
    
    def monitor_canary(self, model_name: str, canary_version: str):
        """Monitor canary deployment performance"""
        # This would run as a separate process in production
        import time
        
        for i in range(10):  # Monitor for 10 cycles
            time.sleep(30)  # Wait 30 seconds between checks
            
            metrics = self.metrics_collector.get_version_metrics(
                model_name, canary_version
            )
            
            # Check if canary is performing well
            if metrics.get('error_rate', 0) > 0.05:  # 5% error threshold
                print(f"Canary deployment failed for {canary_version}")
                self.rollback_canary(model_name, canary_version)
                break
            elif metrics.get('avg_inference_time', 0) > 1.0:  # 1 second threshold
                print(f"Canary performance degraded for {canary_version}")
                self.rollback_canary(model_name, canary_version)
                break
            
            print(f"Canary check {i+1}/10 passed for {canary_version}")
        
        # If all checks pass, promote canary
        self.promote_canary(model_name, canary_version)
    
    def rollback_canary(self, model_name: str, canary_version: str):
        """Rollback failed canary deployment"""
        print(f"Rolling back canary deployment for {canary_version}")
        self.update_traffic_split(model_name, {"v1.0": 100.0})
    
    def promote_canary(self, model_name: str, canary_version: str):
        """Promote successful canary to full traffic"""
        print(f"Promoting canary {canary_version} to full traffic")
        self.update_traffic_split(model_name, {canary_version: 100.0})

class MetricsCollector:
    def __init__(self):
        self.predictions = []
        
    def record_prediction(self, model_name: str, model_version: str, 
                         inference_time: float, input_size: int):
        """Record prediction metrics"""
        self.predictions.append({
            'timestamp': time.time(),
            'model_name': model_name,
            'model_version': model_version,
            'inference_time': inference_time,
            'input_size': input_size
        })
    
    def get_version_metrics(self, model_name: str, version: str) -> Dict[str, float]:
        """Get aggregated metrics for a model version"""
        version_predictions = [
            p for p in self.predictions 
            if p['model_name'] == model_name and p['model_version'] == version
        ]
        
        if not version_predictions:
            return {}
        
        inference_times = [p['inference_time'] for p in version_predictions]
        
        return {
            'avg_inference_time': np.mean(inference_times),
            'p95_inference_time': np.percentile(inference_times, 95),
            'request_count': len(version_predictions),
            'error_rate': 0.0  # Simplified - would track actual errors
        }

# Example usage
serving_system = MultiModelServing()

# Register model versions
v1_model = ModelVersion(
    name="fraud_detection",
    version="v1.0",
    model_path="models/fraud_v1.pkl",
    traffic_percentage=90.0,
    performance_metrics={"accuracy": 0.92}
)

v2_model = ModelVersion(
    name="fraud_detection", 
    version="v2.0",
    model_path="models/fraud_v2.pkl",
    traffic_percentage=10.0,
    performance_metrics={"accuracy": 0.94}
)

serving_system.register_model(v1_model)
serving_system.register_model(v2_model)

# Start canary deployment
serving_system.canary_deployment("fraud_detection", "v2.0", canary_percentage=5.0)
\`\`\`

## 4. Advanced Monitoring & Observability

### Comprehensive Model Monitoring
\`\`\`python
import prometheus_client
from prometheus_client import Counter, Histogram, Gauge
import structlog
from typing import Dict, List
import numpy as np
from dataclasses import dataclass
from datetime import datetime

@dataclass
class AlertRule:
    metric_name: str
    threshold: float
    operator: str  # >, <, >=, <=, ==
    duration: int  # seconds
    severity: str  # critical, warning, info

class MLMonitoringSystem:
    def __init__(self):
        self.logger = structlog.get_logger()
        self.setup_metrics()
        self.alert_rules = []
        self.active_alerts = {}
        
    def setup_metrics(self):
        """Setup Prometheus metrics"""
        self.prediction_counter = Counter(
            'ml_predictions_total',
            'Total number of predictions made',
            ['model_name', 'model_version', 'prediction_class']
        )
        
        self.inference_duration = Histogram(
            'ml_inference_duration_seconds',
            'Time spent on model inference',
            ['model_name', 'model_version']
        )
        
        self.model_accuracy = Gauge(
            'ml_model_accuracy',
            'Current model accuracy',
            ['model_name', 'model_version']
        )
        
        self.data_drift_score = Gauge(
            'ml_data_drift_score',
            'Data drift detection score',
            ['model_name', 'feature_name']
        )
        
        self.prediction_confidence = Histogram(
            'ml_prediction_confidence',
            'Confidence scores of predictions',
            ['model_name', 'model_version']
        )
    
    def log_prediction(self, model_name: str, model_version: str, 
                      prediction: int, confidence: float, 
                      inference_time: float):
        """Log a single prediction with metrics"""
        
        # Update Prometheus metrics
        self.prediction_counter.labels(
            model_name=model_name,
            model_version=model_version,
            prediction_class=str(prediction)
        ).inc()
        
        self.inference_duration.labels(
            model_name=model_name,
            model_version=model_version
        ).observe(inference_time)
        
        self.prediction_confidence.labels(
            model_name=model_name,
            model_version=model_version
        ).observe(confidence)
        
        # Structured logging
        self.logger.info(
            "prediction_made",
            model_name=model_name,
            model_version=model_version,
            prediction=prediction,
            confidence=confidence,
            inference_time=inference_time,
            timestamp=datetime.now().isoformat()
        )
        
        # Check for low confidence alerts
        if confidence < 0.7:
            self.trigger_alert(
                "low_confidence_prediction",
                f"Low confidence prediction: {confidence}",
                severity="warning"
            )
    
    def update_model_performance(self, model_name: str, model_version: str,
                               accuracy: float, precision: float, recall: float):
        """Update model performance metrics"""
        
        self.model_accuracy.labels(
            model_name=model_name,
            model_version=model_version
        ).set(accuracy)
        
        self.logger.info(
            "model_performance_updated",
            model_name=model_name,
            model_version=model_version,
            accuracy=accuracy,
            precision=precision,
            recall=recall
        )
        
        # Check performance degradation
        if accuracy < 0.85:
            self.trigger_alert(
                "model_performance_degradation",
                f"Model accuracy dropped to {accuracy}",
                severity="critical"
            )
    
    def detect_data_drift(self, model_name: str, feature_data: Dict[str, np.ndarray],
                         reference_data: Dict[str, np.ndarray]):
        """Detect and log data drift"""
        from scipy import stats
        
        for feature_name, current_values in feature_data.items():
            if feature_name in reference_data:
                reference_values = reference_data[feature_name]
                
                # Calculate drift score using KS test
                statistic, p_value = stats.ks_2samp(reference_values, current_values)
                drift_score = 1 - p_value  # Higher score = more drift
                
                # Update metric
                self.data_drift_score.labels(
                    model_name=model_name,
                    feature_name=feature_name
                ).set(drift_score)
                
                self.logger.info(
                    "data_drift_checked",
                    model_name=model_name,
                    feature_name=feature_name,
                    drift_score=drift_score,
                    p_value=p_value
                )
                
                # Alert on significant drift
                if drift_score > 0.8:
                    self.trigger_alert(
                        "data_drift_detected",
                        f"Significant drift in feature {feature_name}: {drift_score}",
                        severity="warning"
                    )
    
    def add_alert_rule(self, rule: AlertRule):
        """Add a new alerting rule"""
        self.alert_rules.append(rule)
        self.logger.info("alert_rule_added", rule=rule)
    
    def trigger_alert(self, alert_type: str, message: str, severity: str):
        """Trigger an alert"""
        alert_key = f"{alert_type}_{severity}"
        
        if alert_key not in self.active_alerts:
            self.active_alerts[alert_key] = {
                'type': alert_type,
                'message': message,
                'severity': severity,
                'timestamp': datetime.now(),
                'count': 1
            }
            
            self.logger.error(
                "alert_triggered",
                alert_type=alert_type,
                message=message,
                severity=severity
            )
            
            # Send to alerting system (Slack, PagerDuty, etc.)
            self.send_alert_notification(alert_type, message, severity)
        else:
            # Increment existing alert count
            self.active_alerts[alert_key]['count'] += 1
    
    def send_alert_notification(self, alert_type: str, message: str, severity: str):
        """Send alert to notification system"""
        # Integration with Slack, PagerDuty, etc.
        print(f"🚨 ALERT [{severity.upper()}]: {alert_type} - {message}")
    
    def resolve_alert(self, alert_type: str, severity: str):
        """Resolve an active alert"""
        alert_key = f"{alert_type}_{severity}"
        if alert_key in self.active_alerts:
            del self.active_alerts[alert_key]
            self.logger.info(
                "alert_resolved",
                alert_type=alert_type,
                severity=severity
            )

# Example usage
monitoring = MLMonitoringSystem()

# Add alert rules
monitoring.add_alert_rule(AlertRule(
    metric_name="ml_model_accuracy",
    threshold=0.85,
    operator="<",
    duration=300,  # 5 minutes
    severity="critical"
))

# Log predictions with monitoring
for i in range(100):
    monitoring.log_prediction(
        model_name="fraud_detection",
        model_version="v2.0",
        prediction=1,
        confidence=0.92,
        inference_time=0.05
    )

# Update performance metrics
monitoring.update_model_performance(
    model_name="fraud_detection",
    model_version="v2.0",
    accuracy=0.94,
    precision=0.91,
    recall=0.89
)
\`\`\`

## Summary
You've mastered advanced MLOps techniques essential for enterprise-scale machine learning operations, including feature stores, automated retraining, multi-model serving, and comprehensive monitoring.
`,
  objectives: [
    'Implement feature stores for consistent feature engineering',
    'Set up automated model retraining pipelines',
    'Deploy models using canary and A/B testing strategies',
    'Build comprehensive monitoring and alerting systems',
    'Design fault-tolerant ML architectures'
  ],
  quiz: {
    questions: [
      {
        id: 'q1',
        question: 'What is the primary benefit of using a feature store?',
        options: [
          'Faster model training',
          'Consistent feature engineering across training and serving',
          'Better model accuracy',
          'Reduced infrastructure costs'
        ],
        correctAnswer: 1,
        explanation: 'Feature stores ensure consistency between training and serving environments, preventing training-serving skew and improving model reliability.'
      },
      {
        id: 'q2',
        question: 'In a canary deployment, what happens if the canary fails health checks?',
        options: [
          'The deployment continues anyway',
          'Traffic is automatically rolled back to the previous version',
          'The system shuts down',
          'All models are retrained'
        ],
        correctAnswer: 1,
        explanation: 'Canary deployments include automatic rollback mechanisms that redirect traffic back to the stable version if the canary fails performance or health checks.'
      }
    ]
  }
};